import { CSmartPagination, CSmartTable } from '@coreui/react-pro'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useState } from 'react'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import {
  getCSmartTableColumnData,
  getExactDiffFromCurrentTimeDayJs,
} from 'src/consumer/helpers/utilFunctions'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { getRequestString } from './util'
import { Chip } from '@mui/material'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import capitalize from 'lodash/capitalize'

dayjs.extend(utc)

export default function PatientTherapyActions() {
  const [eventTriggerActions, setEventTriggerActions] = useState([])
  const [mentorsList, setMentorsList] = useState([])
  const [loading, setLoading] = useState(false)
  const [paginationData, setPaginationData] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 20,
  })
  const [filters, setFilters] = useState({
    mentorId: '',
  })

  async function getTherapyActionsList({
    page = paginationData?.page,
    mentorId = filters?.mentorId,
  } = {}) {
    const resp = await getSPDataWithParams('/api/secure/cc/support/event-trigger-action/list', {
      page: page,
      size: 20,
      sort: 'triggeringEventTime,desc',
      triggeringEventMentor: mentorId,
    })
    if (resp.status === 200) {
      setEventTriggerActions(resp.data.content)
      setPaginationData({
        page: resp.data.pageable?.pageNumber,
        totalPages: resp.data.totalPages,
        totalElements: resp.data.totalElements,
        size: resp.data.pageable?.pageSize,
      })
    }
  }

  async function getMentorsList() {
    const resp = await getSPDataWithParams('/api/secure/cc/support/all-doctors-therapists/list', {})
    if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const mentorsList = getDataMapFromCommonResponse(resp)?.mentor
      setMentorsList(mentorsList)
    }
  }

  async function getDataWithSortPageOrSize({ page = paginationData?.page, mentorId }) {
    setPaginationData({ ...paginationData, page: page })
    setFilters((prev) => ({
      ...prev,
      mentorId: mentorId === null ? undefined : mentorId || prev.mentorId,
    }))
    setLoading(true)
    await getTherapyActionsList({ page: page, mentorId })
    setLoading(false)
  }

  async function setup() {
    setLoading(true)
    await Promise.all([getTherapyActionsList(), getMentorsList()])
    setLoading(false)
  }
  useEffect(() => {
    setup()
  }, [])

  const scopedColumns = {
    triggeringTime: (item) => (
      <td>
        <div className="d-flex flex-column gap-1">
          <div>{dayjs.utc(item?.triggeringEventTime).local().format('DD MMM YYYY hh:mm A')}</div>
          <div
            style={{
              color: '#00000080',
            }}
          >
            {getExactDiffFromCurrentTimeDayJs(dayjs.utc(item?.triggeringEventTime).local())}
          </div>
        </div>
      </td>
    ),
    actionRequest: (item) => <td>{getRequestString(item)}</td>,
    requestedBy: (item) => {
      const mentor = mentorsList.find((m) => m.id === item?.triggeringEventMentorId)
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            <span>
              <strong>Practitioner:</strong> {mentor?.name || item?.requestedBy}
            </span>
            <div>
              <strong>Clinic:</strong> {item?.clinicName}
            </div>
          </div>
        </td>
      )
    },
    patient: (item) => {
      return (
        <td>
          <div className="d-flex flex-column gap-1">
            <div>
              ({item?.patientNumber}) {item?.patientName}
            </div>
            <div>
              <strong>Age:</strong> {getAgeFromDobWithTime(item?.patientDob)}
            </div>
            <div>
              <strong>Gender:</strong> {capitalize(item?.patientGender)}
            </div>
          </div>
        </td>
      )
    },
    status: (item) => (
      <td>
        <Chip
          label={item?.status?.toUpperCase()}
          color={
            item?.status?.toLowerCase() === 'pending'
              ? 'warning'
              : item?.status?.toLowerCase() === 'completed'
              ? 'success'
              : item?.status?.toLowerCase() === 'cancelled'
              ? 'error'
              : 'default'
          }
        />
      </td>
    ),
  }
  return (
    <>
      <SupportHeader heading={'Therapy Actions'} />
      <div className="w-100 p-3">
        <div className="w-100 d-flex justify-content-between align-items-end mb-3">
          <div className="w-25">
            <CustomSelect
              value={filters?.mentorId}
              options={mentorsList?.sort((a, b) => a?.name?.localeCompare(b?.name))}
              getOptionLabel={(option) => option?.name}
              getOptionValue={(option) => option?.id}
              onChangeEvent={(e) =>
                getDataWithSortPageOrSize({ page: 0, mentorId: e.target.value ?? null })
              }
              label={'Requested By'}
              isClearable
              placeholder={'Select Requested By'}
              selectType={selectTypes.string}
              id={'mentorId'}
            />
          </div>
          {paginationData?.totalPages > 1 && (
            <CSmartPagination
              align="end"
              activePage={paginationData?.page + 1}
              pages={paginationData?.totalPages}
              onActivePageChange={(page) => getDataWithSortPageOrSize({ page: page - 1 })}
            />
          )}
        </div>
        <CSmartTable
          items={eventTriggerActions}
          columns={columns}
          loading={loading}
          tableProps={{
            hover: true,
            striped: true,
            responsive: true,
          }}
          itemsPerPage={10000000000000000}
          scopedColumns={scopedColumns}
        />
      </div>
    </>
  )
}

const columns = [
  getCSmartTableColumnData({
    key: 'triggeringTime',
    label: 'Requested Time',
    _style: { width: '20%' },
    _props: { className: 'fw-semibold' },
    filter: false,
    sorter: false,
  }),
  getCSmartTableColumnData({
    key: 'actionRequest',
    label: 'Request',
    _style: { width: '20%' },
    filter: false,
    sorter: false,
  }),
  getCSmartTableColumnData({
    key: 'requestedBy',
    label: 'Requested By',
    _style: { width: '15%' },
    filter: false,
    sorter: false,
  }),
  getCSmartTableColumnData({
    key: 'patient',
    label: 'Patient',
    _style: { width: '15%' },
    filter: false,
    sorter: false,
  }),
  getCSmartTableColumnData({
    key: 'status',
    label: 'Status',
    _style: { width: '15%' },
    filter: false,
    sorter: false,
  }),
]
